﻿<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import 'video.js/dist/video-js.css'
import '@/assets/css/videojs.css'
import StarterFooter from "@/views/starter/StarterFooter.vue";
import { useRouter } from "vue-router";
import axios from "axios";

const router = useRouter();
let newPageUrl = ref('')
let urlState = ref({
  valid: true,
  busy: false,
  message: ''
})

const activeExample = ref('before')
const comparisonSection = ref(null)

const activeFaq = ref(null)

function toggleFaq(id) {
  if (activeFaq.value === id) {
    activeFaq.value = null
  } else {
    activeFaq.value = id
  }
}

let observer = null

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      // If the section is intersecting our trigger zone (top 20% of viewport)
      if (entry.isIntersecting) {
        activeExample.value = 'after'
      } else {
        // If we scroll back up and the element is no longer in the trigger zone
        // (and it's below the trigger zone), switch back to 'before'
        if (entry.boundingClientRect.top > 0) {
          activeExample.value = 'before'
        }
      }
    })
  }, {
    // Only trigger when the element hits the top portion of the screen
    // rootMargin: 'top right bottom left'
    // -80% bottom margin means the "active area" is only the top 20% of the viewport
    rootMargin: '0px 0px -80% 0px',
    threshold: 0
  })

  if (comparisonSection.value) {
    observer.observe(comparisonSection.value)
  }
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})

async function StartForFree() {

  urlState.value.busy = false;
  // check if newPageUrl is a valid url
  if (!newPageUrl.value || newPageUrl.value.length < 5 || newPageUrl.value.indexOf('.') === -1) {
    urlState.value.valid = false;

    return;
  }
  urlState.value.valid = true;

  let url = newPageUrl.value
  try {
    urlState.value.busy = true;
    let result = await axios.post(`websites/validate-url/`, {
      url: url
    })
    if (result.data.success) {
      urlState.value.busy = false
      await router.push({name: 'auth-signup', query: {url: result.data.website}})
    }
  } catch (e) {
    urlState.value.message = 'Please enter a valid url.'
    urlState.value.valid = false;
    console.log(e)
  } finally {
    urlState.value.busy = false
  }


}

</script>

<template>
  <div class="landing">
    <router-view></router-view>

    <section class="landing-hero">
      <div class="container">
        <div class="hero-grid">
          <div class="hero-copy p-2">
            <h1>Is your website ready for the <span class="text-highlight">AI Era</span>?</h1>
            <p>
            <span class="text-highlight fw-bold"> Don't let ChatGPT hallucinate about your product.</span>
            </p>

            <form class="hero-form" @submit.prevent="StartForFree">
              <div class="url-input-group">
                <i class="fa-solid fa-link text-muted fs-4 ms-2"></i>
                <input
                  type="url"
                  name="url"
                  v-model="newPageUrl"
                  @input="urlState.valid=true"
                  class="form-control form-control-lg"
                  placeholder="Enter your website URL"
                  required
                >
                <button class="btn btn-primary" :disabled="urlState.busy" aria-current="page">
                  <template v-if="!urlState.busy"><span class="text-nowrap">Analyze My Site</span></template>
                  <template v-else>
                    <span class="text-nowrap"><span class="spinner-border spinner-border-sm"></span> Validating..</span>
                  </template>
                </button>
              </div>              
              <div class="form-hint" v-show="!urlState.valid">
                Please enter a valid URL
              </div>
              <div class="mt-1 ms-2 text-muted fs-6">
                Check your homepage for free or <a href="https://pagewatch.ai/s-aevwuu46ou0m/5xhstftk/dashboard" target="_blank" class="text-primary text-decoration-underline">view a live demo</a>
              </div>
            </form>


          </div>

          <div class="hero-visual">
            <div class="visual-card">
              <img src="/assets/media/landing/dashboard.png" alt="PageWatch dashboard showing audit results for Spelling & Grammar, Browser support, Dead links, and Broken Images with an overall score of 78" />
            </div>
            <div class="floating-card">
              <div class="floating-header">
                <div class="score">78</div>
                <div>
                  <div class="floating-title">AI Readiness</div>
                  <div class="floating-sub"><b>1</b> issue found</div>
                </div>
              </div>
              <div class="floating-list">
                <div class="floating-item">
                  <span class="status-dot status-warn"></span>
                 ChatGPT cannot see your pricing.
                </div>
              </div>
            </div>
            <div class="floating-card floating-card-right">
              <div class="floating-title">Watched prompts</div>
              <div class="floating-sub mb-1">Tracking 12 Questions</div>
              <div class="mini-bars">
                <span></span>
                <span></span>
                <span></span>
                <span style="background-color: red"></span>
              </div>
              <div class="floating-sub mt-2 text-danger">1 Missing Answer</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="landing-section section-soft" ref="comparisonSection">
      <div class="container">
        <div class="split-grid">
          <div>
            <h2>Make sure AI understands your pages</h2>
            <p>
              Every day, millions of users ask ChatGPT and Gemini for recommendations.<br>
              <span class="fw-bolder text-highlight">AI models won't recommend your service if they can't understand it.</span>
            </p>
            <div class="mt-4">
              <div class="mb-4 d-flex align-items-start">
                <span class="status-dot status-ok mt-1"></span>
                <div>
                  <span class="fw-bold d-block mb-1">We compare your page vs <span class="text-highlight">what a chatbot sees</span></span>
                  <p class="text-muted mb-2">
                    We analyze your pages exactly as a human would—evaluating images and fully rendered layouts, to see what a chatbot could miss.
                  </p>
                </div>
              </div>
              <div class="mb-3 d-flex align-items-start">
                <span class="status-dot status-ok mt-1"></span>
                <div>
                  <span class="fw-bold d-block mb-1">Simple to implement fixes</span>
                  <p class="text-muted mb-2">
                    We tell you exactly the issues we find and how to fix them on your pages.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="media-column">
            <div class="text-center mb-0">
              <div class="query-box mb-0 text-start d-inline-block" style="z-index: 2;">
                <div class="pill position-absolute" style="top: 8px; right: 8px; font-size: 0.55rem; padding: 2px 6px; line-height: 1; background: #10a37f; color: #fff; border: none; letter-spacing: 0.05em; text-transform: uppercase;">Live example</div>
                <div class="query-label mb-1">
                  <img src="/assets/images/ChatGPT_logo.svg" alt="ChatGPT" style="width: 18px; height: 18px; vertical-align: -4px;" class="me-1 rounded-1" />
                  ChatGPT Prompt
                </div>
                <div class="query-text me-5">
                  "List the features found on https://acme-on-call.com/compare<span class="text-success" v-if="activeExample === 'after'">_v2</span>"
                </div>
              </div>
            </div>
            <div class="connecting-line-container d-none d-lg-block">
              <svg width="100%" height="40" viewBox="0 0 100 40" preserveAspectRatio="none">
                <path 
                  :d="activeExample === 'before' ? 'M 50 0 L 50 20 L 25 20 L 25 40' : 'M 50 0 L 50 20 L 75 20 L 75 40'" 
                  fill="none" 
                  :stroke="activeExample === 'before' ? '#ff9f43' : '#10a37f'" 
                  stroke-width="2" 
                  style="transition: all 0.3s ease;" 
                />
              </svg>
            </div>
            <div class="compare-grid">
              <div class="compare-card" :class="{ 'compare-card-warn': activeExample === 'before' }" @click="activeExample = 'before'">
                <div class="compare-header">
                  <span class="status-dot status-warn"></span>
                  <span class="compare-label text-muted me-2">Before PageWatch Changes</span>
                </div>
                <img src="/assets/media/landing/chat_comparison_old.png" alt="Table showing features ChatGPT misses: 24/7 on-call routing (?), SMS + voice escalation (?), 60+ integrations (?), Incident postmortems (?), and White-glove onboarding (?)" class="rounded border" />
                <div class="compare-note text-warning-emphasis d-flex align-items-center gap-2">
                  <i class="fa-solid fa-triangle-exclamation"></i>
                  <span>AI misses half the features due to poor semantics.</span>
                </div>
              </div>
              <div class="compare-card" :class="{ 'compare-card-success': activeExample === 'after' }" @click="activeExample = 'after'">
                <div class="compare-header">
                  <span class="status-dot status-ok"></span>
                  <span class="compare-label me-2">After PageWatch Changes</span>
                </div>
                <img src="/assets/media/landing/chat_comparison_new.png" alt="Table showing features correctly identified by PageWatch: 24/7 on-call routing (Yes), SMS + voice escalation (Yes), 60+ integrations (Yes), Incident postmortems (Yes), and White-glove onboarding (Yes)"  class="rounded border" />
                <div class="compare-note text-success d-flex align-items-center gap-2">
                  <i class="fa-solid fa-circle-check"></i>
                  <span>Complete, accurate feature list extracted by AI.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="landing-section" v-if="false">
      <div class="container">
        <div class="split-grid reverse">
          <div>
            <h2>Track <span class="text-highlight">Real AI</span> Answers</h2>
            <p>
              Stop guessing what ChatGPT says about you. Define key questions, and we'll track how leading models
              answer them over time, alerting you to regressions.
            </p>
            <div class="mt-4">
              <div class="mb-3 d-flex align-items-start">
                <span class="status-dot status-ok mt-1"></span>
                <span>Define custom prompts to monitor.</span>
              </div>
              <div class="mb-3 d-flex align-items-start">
                <span class="status-dot status-ok mt-1"></span>
                <span>Get checks and alerts when AI answers drift from your reference.</span>
              </div>
            </div>
          </div>
          <div class="media-column">
            <div class="media-frame shadow-lg">
              <img src="/assets/media/landing/watched_prompts_dashboard.png" alt="Dashboard showing a side-by-side comparison of a Reference Answer against responses from ChatGPT and Gemini for specific prompts" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="landing-section">
      <div class="container">
        <div class="split-grid reverse">
          <div>
            <h2>Protect Your <span class="text-highlight">Important</span> Pages</h2>
            <p>
              Beyond LLM monitoring—PageWatch provides a comprehensive automated QA suite.
              We ensure your most critical pages remain polished and functional for both human visitors and AI agents.
            </p>
            <div class="mt-4">
              <div class="mb-3 d-flex align-items-start">
                <span class="status-dot status-ok mt-1"></span>
                <span><strong>Full-Site QA:</strong> Spot typos, broken links, and readability issues across every page.</span>
              </div>
              <div class="mb-3 d-flex align-items-start">
                <span class="status-dot status-ok mt-1"></span>
                <span><strong>Technical Integrity:</strong> Surface console errors and resource failures that impact both users and bots.</span>
              </div>
              <div class="mb-3 d-flex align-items-start">
                <span class="status-dot status-ok mt-1"></span>
                <span><strong>Visual Consistency:</strong> Automatically detect layout shifts and element collisions on all devices.</span>
              </div>
            </div>
            <div class="mt-4">
              <router-link :to="{name: 'auth-signup'}" class="btn btn-outline-primary">
                Start monitoring
              </router-link>
            </div>
          </div>
          <div class="media-column">
            <div class="media-frame shadow-lg">
              <img src="/assets/media/landing/all-pages.png" alt="Dashboard showing health scores and issue breakdowns for site pages including Homepage, about page, and pricing page"  />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="landing-section section-soft">
      <div class="container">
        <div class="split-grid">
          <div>
            <h2>Stay on top of <span class="text-highlight">changes</span></h2>
            <p>
              Websites are dynamic. Even a minor layout shift or content update can disrupt how AI models perceive your
              brand. PageWatch monitors your pages for visual and content regressions.
            </p>
            <div class="row mt-4">
              <div class="col-sm-12 mb-3">
                <div class="d-flex align-items-center gap-2 mb-2">
                  <span class="status-dot mb-0" style="width: 12px; height: 12px; background: #5a9cb9; border-radius: 3px; margin: 0;"></span>
                  <span class="fw-bold">Timeline</span>
                </div>
                <p class="small text-muted mb-0">Navigate a complete history of changes to pinpoint exactly when a regression occurred.</p>
              </div>
            </div>
          </div>
          <div class="media-column">
            <div class="media-frame shadow-lg">
              <img src="/assets/media/landing/page_timeline.png" alt="Page Timeline and Change Tracking" />
            </div>
          </div>
        </div>
      </div>
    </section>





    <section class="landing-section section-soft" id="pricing">
      <div class="container">
        <div class="section-header">
          <div>
            <h2>Plans that <span class="text-highlight">scale</span> with your pages.</h2>
            <p>Protect a few key pages or watch your entire site.</p>
          </div>
        </div>

        <div class="pricing-grid">
          <div class="pricing-card">
            <div class="pricing-head">
              <h3>Essential</h3>
              <span>For smaller sites and startups</span>
            </div>
            <div class="pricing-price">$29<span>/month</span></div>
            <router-link :to="{name: 'auth-signup'}" class="btn btn-orange w-100">Start For Free</router-link>
            <ul>
              <li>200 Monthly Credits
                <ul class="sub-features">
                  <li><span class="sr-only">Included:</span>AI Readiness Checks</li>
                  <li><span class="sr-only">Included:</span>Mobile, Tablet and Desktop Audits</li>
                  <li><span class="sr-only">Included:</span>Visual Timeline</li>
                  <li><span class="sr-only">Included:</span>Full QA Audits</li>
                </ul>
              </li>


            </ul>
          </div>
          <div class="pricing-card featured">
            <div class="pricing-badge">Most Popular</div>
            <div class="pricing-head">
              <h3>Standard</h3>
              <span>For SMEs and small teams</span>
            </div>
            <div class="pricing-price">$99<span>/month</span></div>
            <router-link :to="{name: 'auth-signup'}" class="btn btn-orange w-100">Start For Free</router-link>
            <ul>
              <li>800 Monthly Credits
                <ul class="sub-features">
                  <li><span class="sr-only">Included:</span>AI Readiness Checks</li>
                  <li><span class="sr-only">Included:</span>Mobile, Tablet and Desktop Audits</li>
                  <li><span class="sr-only">Included:</span>Visual Timeline</li>
                  <li><span class="sr-only">Included:</span>Full QA Audits</li>
                </ul>
              </li>

            </ul>
          </div>
          <div class="pricing-card">
            <div class="pricing-head">
              <h3>Business</h3>
              <span>For agencies and larger businesses</span>
            </div>
            <div class="pricing-price">$249<span>/month</span></div>
            <router-link :to="{name: 'auth-signup'}" class="btn btn-orange w-100">Start For Free</router-link>
            <ul>
              <li>2200 Monthly Credits
                <ul class="sub-features">
                  <li><span class="sr-only">Included:</span>AI Readiness Checks</li>
                  <li><span class="sr-only">Included:</span>Mobile, Tablet and Desktop Audits</li>
                  <li><span class="sr-only">Included:</span>Visual Timeline</li>
                  <li><span class="sr-only">Included:</span>Full QA Audits</li>
                </ul>
              </li>

            </ul>
          </div>
        </div>
      </div>
    </section>
    <section class="landing-section" id="faq">
      <div class="container">
        <div class="section-header text-center">
          <h2>Frequently Asked <span class="text-highlight">Questions</span></h2>
        </div>

        <div class="faq-container">
          <div class="faq-item" :class="{ 'active': activeFaq === 2 }">
            <button class="faq-toggle" @click="toggleFaq(2)">
              <span>What are credits?</span>
              <i class="fa-solid fa-chevron-down toggle-icon"></i>
            </button>
            <div class="faq-content">
              <div class="faq-body">
                <p>Each plan includes a monthly allotment of credits that you can use to audit your website's pages.</p>
                <p>There are two kinds of scans available:</p>
                <ul>
                  <li><strong>Full Scan (5 credits per page):</strong> Includes comprehensive QA audits and AI readiness checks to ensure your content is optimized for AI agents and search engines.</li>
                  <li><strong>Capture Only (2 credits per page):</strong> Check for visual regressions on desktop, mobile and tablet devices. </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="faq-item" :class="{ 'active': activeFaq === 1 }">
            <button class="faq-toggle" @click="toggleFaq(1)">
              <span>Is there a free trial?</span>
              <i class="fa-solid fa-chevron-down toggle-icon"></i>
            </button>
            <div class="faq-content">
              <div class="faq-body">
                <p>Yes, we provide a free trial for new users. Sign up today to start your trial, no credit card required.</p>
              </div>
            </div>
          </div>
          <div class="faq-item" :class="{ 'active': activeFaq === 3 }">
            <button class="faq-toggle" @click="toggleFaq(3)">
              <span>How do I cancel my subscription?</span>
              <i class="fa-solid fa-chevron-down toggle-icon"></i>
            </button>
            <div class="faq-content">
              <div class="faq-body">
                <p>You can cancel your subscription at any time through your account settings.</p>
              </div>
            </div>
          </div>
        </div>


      </div>
    </section>
    <section class="landing-cta">
      <div class="container">
        <div class="cta-card shadow-lg">
          <div class="testimonial">
            <div class="testimonial-quote">
              <i class="fa-solid fa-quote-left d-block mb-4 opacity-50" style="font-size: 2.5rem;"></i>
              "The PageWatch tool is incredible. It actually out-performed a manual audit that a hired large agency did for one of my most recent sites"
            </div>
            <div class="testimonial-meta justify-content-center">
              <img src="/assets/media/landing/paulhoft.png" class="avatar" alt="Paul Hoft">
              <div class="text-start">
                <div class="testimonial-name">Paul Hoft</div>
                <div class="testimonial-role">ProductHunt</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <starter-footer/>
  </div>
</template>
<style>
.landing {
  --landing-bg: #f7f9fc;
  --landing-soft: #eef2f7;
  --landing-ink: #1b2430;
  --landing-muted: #5c6b7a;
  --landing-accent: #1d63ff;
  --landing-accent-dark: #1549b8;
  --landing-teal: #23b78a;
  --landing-warn: #ff9f43;
  background: var(--landing-bg);
  color: var(--landing-ink);
  font-family: 'Source Sans 3', sans-serif;
}

.landing h1,
.landing h2,
.landing h3 {
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: -0.02em;
}

.landing-hero {
  background:
    radial-gradient(circle at 20% 20%, rgba(29, 99, 255, 0.12), transparent 45%),
    radial-gradient(circle at 80% 10%, rgba(35, 183, 138, 0.12), transparent 45%),
    linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%);
  padding: 120px 0 80px;
  position: relative;
  overflow: hidden;
}

.hero-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 48px;
  align-items: center;
}

.hero-copy h1 {
  font-size: clamp(2.5rem, 4vw, 3.8rem);
  margin-bottom: 1rem;
}

.hero-copy p {
  font-size: 1.1rem;
  color: var(--landing-muted);
  margin-bottom: 1.5rem;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(29, 99, 255, 0.1);
  color: var(--landing-accent);
  font-weight: 600;
  margin-bottom: 1rem;
}

.hero-form {
  margin-bottom: 1rem;
}

.url-input-group {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  padding: 6px 8px;
  border-radius: 14px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(15, 23, 42, 0.06);
}

.url-input-group input {
  border: none;
  box-shadow: none;
}

.form-hint {
  margin-top: 8px;
  color: #d11a2a;
  font-size: 0.9rem;
}

.hero-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 1.5rem;
}

.meta-chip {
  background: #fff;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 0.9rem;
  display: inline-flex;
  gap: 6px;
  align-items: center;
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.06);
}

.trust-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-top: 2rem;
}

.trust-card {
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.07);
}

.trust-label {
  font-size: 0.85rem;
  color: var(--landing-muted);
}

.trust-value {
  font-size: 1.8rem;
  font-weight: 700;
}

.trust-sub {
  font-size: 0.85rem;
  color: var(--landing-muted);
}

.hero-visual {
  position: relative;
}

.visual-card {
  background: #fff;
  border-radius: 24px;
  padding: 12px;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.18);
}

.visual-card img {
  width: 100%;
  border-radius: 16px;
}

.floating-card {
  position: absolute;
  left: -40px;
  bottom: 80px;
  background: #fff;
  border-radius: 18px;
  padding: 16px;
  width: 260px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.12);
}

.floating-card-right {
  left: auto;
  right: -30px;
  top: 30px;
  background: #fff;
  height:160px;
  width: 220px;
}

.floating-header {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}

.score {
  background: #FEF7E0;
  color: #DE7F00;
  font-weight: 700;
  border-radius: 12px;
  padding: 8px 12px;
  font-size: 1.2rem;
}

.floating-title {
  font-weight: 600;
}

.floating-sub {
  color: var(--landing-muted);
  font-size: 0.85rem;
}

.floating-list {
  display: grid;
  gap: 8px;
  font-size: 0.85rem;
  color: var(--landing-muted);
}

.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 12px;
  flex-shrink: 0;
}

.status-ok {
  background: var(--landing-teal);
}

.status-warn {
  background: var(--landing-warn);
}

.text-highlight {
  color: #f38f2d;
}

.mini-bars {
  display: grid;
  gap: 6px;
}

.mini-bars span {
  display: block;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--landing-accent), rgba(29, 99, 255, 0.2));
}

.mini-bars span:nth-child(2) {
  width: 70%;
}

.mini-bars span:nth-child(3) {
  width: 55%;
}

.mini-bars span:nth-child(4) {
  width: 35%;
}

.landing-section {
  padding: 80px 0;
}

.section-soft {
  background: var(--landing-soft);
}

.section-dark {
  background: #0f172a;
  color: #f8fafc;
}

.section-header {
  display: flex;
  gap: 24px;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 40px;
}

.section-header p {
  color: var(--landing-muted);
  margin: 0;
}

.section-dark p {
  color: #cbd5f5;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
}

.feature-card {
  background: #fff;
  border-radius: 18px;
  padding: 24px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  display: grid;
  gap: 12px;
}

.feature-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgba(29, 99, 255, 0.12);
  color: var(--landing-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.feature-badge {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--landing-accent);
}

.split-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 60px;
  align-items: center;
}

.split-grid.reverse {
  direction: rtl;
}

.split-grid.reverse > * {
  direction: ltr;
}

.list-card {
  background: #fff;
  padding: 20px;
  border-radius: 16px;
  display: grid;
  gap: 12px;
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.08);
}

.list-row {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  color: var(--landing-muted);
}

.media-stack {
  display: grid;
  gap: 16px;
}

.media-frame {
  background: #fff;
  border-radius: 20px;
  padding: 10px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.1);
}

.media-frame img {
  width: 100%;
  border-radius: 14px;
}

.media-frame.small {
  max-width: 320px;
  justify-self: end;
}

.pill-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 1.5rem;
}

.pill-row span {
  background: #fff;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 0.85rem;
  color: var(--landing-muted);
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.06);
}

.testimonial {
  display: grid;
  gap: 32px;
  max-width: 800px;
  margin: 0 auto;
}

.testimonial-quote {
  font-size: clamp(1.2rem, 3vw, 1.8rem);
  font-weight: 600;
  line-height: 1.4;
  font-family: 'Space Grotesk', sans-serif;
}

.testimonial-meta {
  display: flex;
  gap: 16px;
  align-items: center;
}

.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.testimonial-name {
  font-weight: 600;
}

.testimonial-role {
  font-size: 0.9rem;
  color: #cbd5f5;
}

.compare-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.query-box {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  position: relative;
}

.query-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 700;
  color: var(--landing-muted);
  margin-bottom: 4px;
}

.query-text {
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  color: var(--landing-ink);
}

.compare-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.compare-card {
  background: #fff;
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.08);
  display: grid;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;
}

.compare-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.12);
}

.compare-card img {
  width: 100%;
  height: auto;
  display: block;
}

.compare-label {
  font-weight: 600;
  color: var(--landing-accent);
}

.compare-card-success {
  border: 1px solid var(--landing-teal) !important;
  box-shadow: 0 20px 40px rgba(35, 183, 138, 0.15) !important;
}

.compare-card-warn {
  border: 1px solid var(--landing-warn) !important;
  box-shadow: 0 20px 40px rgba(255, 159, 67, 0.1) !important;
}

.bg-success-light {
  background-color: rgba(35, 183, 138, 0.1);
}

.connecting-line-container {
  height: 40px;
  pointer-events: none;
  margin: 0 auto;
}

.image-placeholder {
  height: 220px;
  border-radius: 14px;
  background: repeating-linear-gradient(
    135deg,
    rgba(29, 99, 255, 0.08),
    rgba(29, 99, 255, 0.08) 12px,
    rgba(35, 183, 138, 0.08) 12px,
    rgba(35, 183, 138, 0.08) 24px
  );
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--landing-muted);
  font-weight: 600;
}

.compare-note {
  color: var(--landing-muted);
  font-size: 0.9rem;
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
}

.pricing-card {
  background: #fff;
  border-radius: 18px;
  padding: 32px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.pricing-card.featured {
  border: 2px solid var(--landing-warn);
  transform: translateY(-6px);
  position: relative;
}

.pricing-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--landing-warn);
  color: #fff;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.btn-orange {
  background: var(--landing-warn);
  color: #fff;
  border: none;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 12px;
  transition: all 0.2s;
}

.btn-orange:hover {
  background: #f38f2d;
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(255, 159, 67, 0.3);
}

.pricing-head span {
  color: var(--landing-muted);
  font-size: 0.9rem;
}

.pricing-price {
  font-size: 2rem;
  font-weight: 700;
}

.pricing-price span {
  font-size: 0.9rem;
  color: var(--landing-muted);
}

.pricing-card ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: var(--landing-muted);
}

.pricing-card li {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--landing-ink);
  line-height: 1.4;
}

.pricing-card li::before {
  display: none;
}

.pricing-card li.unsupported {
  opacity: 0.5;
}

.pricing-card ul ul {
  padding-left: 0;
  margin-top: 8px;
  width: 100%;
  gap: 8px;
}

.pricing-card ul ul li {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.9rem;
  font-weight: 400;
  color: var(--landing-muted);
  opacity: 1;
}

.pricing-card ul ul li::before {
  display: block;
  content: "\f00c";
  font-family: "Font Awesome 6 Free";
  font-weight: 900;
  color: var(--landing-teal);
  margin-right: 0;
  flex-shrink: 0;
  margin-top: 4px;
  font-size: 0.75rem;
  opacity: 1;
}

.pricing-card ul ul li.unsupported::before {
  content: "\f00d";
  color: var(--landing-muted);
  opacity: 0.5;
}

.feature-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.landing-cta {
  padding: 100px 0;
}

.faq-container {
  max-width: 800px;
  margin: 50px auto 0;
}

.faq-item {
  background: #fff;
  border-radius: 14px;
  margin-bottom: 15px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;
}

.faq-item.active {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
}

.faq-toggle {
  width: 100%;
  padding: 20px 24px;
  background: none;
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  text-align: left;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--landing-ink);
}

.toggle-icon {
  font-size: 0.9rem;
  transition: transform 0.3s ease;
  color: var(--landing-accent);
}

.faq-item.active .toggle-icon {
  transform: rotate(180deg);
}

.faq-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out;
}

.faq-item.active .faq-content {
  max-height: 500px;
}

.faq-body {
  padding: 0 24px 24px;
}

.faq-body p, .faq-body li {
  color: var(--landing-muted);
  line-height: 1.6;
  margin-bottom: 10px;
}

.faq-body ul {
  padding-left: 20px;
  margin-top: 10px;
}

@media (max-width: 768px) {
  .faq-container {
    max-width: 100%;
  }
}

.section-header.text-center {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cta-card {
  background: linear-gradient(135deg, var(--landing-accent), var(--landing-accent-dark));
  border-radius: 40px;
  padding: 80px 40px;
  color: #fff;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(29, 99, 255, 0.2);
}

.cta-card::before {
  content: "";
  position: absolute;
  top: -100px;
  right: -100px;
  width: 300px;
  height: 300px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  pointer-events: none;
}

.cta-actions {
  display: flex;
  gap: 12px;
}

@media (max-width: 991px) {
  .split-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .compare-grid {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin: 0 auto;
  }

  .hero-visual{
    display: none;
  }

  .floating-card {
    width: 100%;
    margin-top: 16px;
  }


  .hero-grid {
    gap: 32px;
  }

  .cta-card {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 575px) {
  .url-input-group {
    flex-direction: column;
    align-items: stretch;
  }

  .url-input-group button {
    width: 100%;
  }

  .hero-meta {
    flex-direction: column;
    align-items: flex-start;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
